import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDocument } from "../hooks/useDocument";
import InvoiceContent from "./InvoiceContent";
import CapabilitiesStyles from "./CapabilitiesStyles";

const PreviewDocument = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();

  const { document, isLoading, isError, error } = useDocument(documentId);

  useEffect(() => {
    if (isError) {
      console.error("Error fetching document:", error);
    }
  }, [isError, error]);

  // Build the full content with cover page, same as DocumentEditor's getFullContent
  const fullContent = useMemo(() => {
    if (!document) return "";

    const docType = document.document_type;
    const content = document.content || "";

    // Strip the document-wrapper closing </div> along with </body></html>
    // so the signature and footer flow inside the white document-wrapper
    // container. Not end-anchored to handle existing docs where the
    // signature was saved after these closing tags.
    const stripClosingHtmlTags = (html) => {
      return html.replace(/<\/div>\s*<\/body>\s*<\/html>/gi, "");
    };

    // Replace empty business signature canvas with rendered cursive text
    // and fill in the date (canvas needs JS to draw, which isn't available here)
    const renderBusinessSignature = (html) => {
      const createdDate = document.created_at
        ? new Date(document.created_at).toLocaleDateString()
        : new Date().toLocaleDateString();
      let result = html.replace(
        /<canvas id="business-signature-pad"[^>]*><\/canvas>/gi,
        '<div class="business-signature-display"><span style="font-family: \'Brush Script MT\', cursive; font-style: italic; font-size: 24px;">Carter James</span></div>'
      );
      result = result.replace(
        /<span id="business-sign-date"><\/span>/gi,
        createdDate
      );
      return result;
    };

    let assembled = "";

    // Process content: strip closing tags and render business signature
    const processContent = (html) => {
      return renderBusinessSignature(stripClosingHtmlTags(html));
    };

    if (docType === "capabilities") {
      assembled += `
        <div class="cover-page">
          <h1>${document.title || ""}</h1>
          ${document.subtitle ? `<h2>${document.subtitle}</h2>` : ""}
          ${document.tagline ? `<p class="tagline">${document.tagline}</p>` : ""}
          ${document.secondary_tagline ? `<p class="secondary-tagline">${document.secondary_tagline}</p>` : ""}
        </div>
      `;
      assembled += processContent(content);
      assembled += `
        <div class="company-footer">
          <div class="company-name">GrayTecknologies LLC</div>
          <div class="company-tagline">Innovative Web Solutions</div>
        </div>
      `;
    } else if (docType === "proposal") {
      assembled += `
        <div class="cover-page">
          <h1>${document.title || ""}</h1>
          ${document.subtitle ? `<h2>${document.subtitle}</h2>` : ""}
          ${document.poc_name ? `<p>Prepared for: ${document.poc_name}</p>` : ""}
          ${document.poc_role ? `<p>${document.poc_role}${document.receiving_company ? `, ${document.receiving_company}` : ""}</p>` : ""}
        </div>
      `;
      assembled += processContent(content);
      assembled += `
        <div class="company-footer">
          <div class="company-name">GrayTecknologies LLC</div>
          <div class="company-tagline">Innovative Web Solutions</div>
        </div>
      `;
    } else {
      // Standard document
      if (document.banner_image) {
        assembled += `<div class="document-banner"><img src="${document.banner_image}" alt="Document Banner" /><div class="banner-title-overlay"><h1 class="banner-title">${document.title}</h1><div class="banner-title-line"></div></div></div>`;
      } else {
        assembled += `<h1 style="margin-top: 1.5em; margin-bottom: 1em;">${document.title || ""}</h1>`;
      }
      assembled += `<div class="document-body">${processContent(content)}`;
      assembled += '<hr class="footer-divider">';
      assembled += `
        <div class="company-branding">
          <p class="company-name">GrayTecknologies LLC</p>
          <p class="company-tagline">Innovative Web Solutions</p>
        </div>
      `;
      assembled += "</div>";
    }

    return assembled;
  }, [document]);

  if (isLoading) {
    return <div>Loading document preview...</div>;
  }

  if (isError || !document) {
    return (
      <div>
        <p>Failed to load the document.</p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Back
        </button>
      </div>
    );
  }

  const isProposalOrCapabilities =
    document.document_type === "proposal" ||
    document.document_type === "capabilities";

  return (
    <div className="fixed inset-0 bg-gray-50 dark:bg-darkbg-900 overflow-auto z-50">
      {isProposalOrCapabilities ? <CapabilitiesStyles /> : null}

      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 px-4 py-2 bg-white dark:bg-darkbg-800 shadow-lg rounded-md flex items-center gap-2 z-50 hover:bg-gray-50 dark:hover:bg-darkbg-700 transition-colors"
      >
        Back
      </button>

      {/* Render the document content */}
      <div className={`max-w-[1200px] mx-auto ${isProposalOrCapabilities ? "px-2 sm:px-4" : "mt-8 p-8 bg-white dark:bg-darkbg-800 shadow-xl rounded-lg"}`}>
        <div
          className="document-content"
          dangerouslySetInnerHTML={{ __html: fullContent }}
        />
      </div>
    </div>
  );
};

export default PreviewDocument;
